










































import { Component, Emit, Prop, Vue } from 'vue-property-decorator';
import { _Boolean } from '@/API';
import { JobItem } from '@/appsync/graphql/job';
import { getImportJobStatus } from '@/appsync/lambda/lambdaExecutionStatusService';
import { generateJobFileUploadLink } from '@/appsync/lambda/uploadFileViaConversionApiService';
import { i18n } from '@/plugins/i18n/i18n';
import { globalSnackbarModule } from '@/store/modules/GlobalSnackbarStore';
import { LoginInfo } from '@/types/type';
import { ImportJobStatus } from '@/utils/consts';

@Component
export default class ImportJobFileDialog extends Vue {
  private jobFile: File | null = null;
  private fileValidationRules = [
    (file: File) => {
      this.jobFile = file;
      return this.isYamlOrCsv;
    },
  ];

  private isImportLoading = false;

  @Prop()
  private showImportJobFileDialog!: boolean;
  @Prop()
  private jobs!: JobItem[];
  @Prop()
  private loginInfo!: LoginInfo;
  @Emit()
  onCloseDialog() {}
  @Emit()
  onStartImport() {}

  private async importJobFromFile() {
    if (this.jobFile === null) return;
    const result = await getImportJobStatus(
      this.loginInfo.tenant,
      this.loginInfo.landscape
    );
    const progress = result == null ? ImportJobStatus.notStarted : result;

    if (progress === ImportJobStatus.started) {
      this.showSnackbarAndHideAfter3Sec(
        i18n.tc('jobScheduler.jobs.importJobAlreadyStarted')
      );
      return;
    }

    this.isImportLoading = true;
    try {
      const { tenant, landscape } = this.loginInfo;
      const signedUploadUrl = await generateJobFileUploadLink(
        tenant,
        landscape,
        this.jobFile
      );
      await fetch(signedUploadUrl.preSignedUrl, {
        method: 'PUT',
        body: this.jobFile,
      });
      this.onStartImport();
      this.onCloseDialog();
      this.jobFile = null;
      globalSnackbarModule.showSnackbarAction(
        i18n.tc('jobScheduler.jobs.importJobStarted')
      );
      globalSnackbarModule.showProgressCircularAction();
      await this.waitForImportJobToFinish();
    } finally {
      this.isImportLoading = false;
    }
  }

  @Emit('import-completed')
  private emitImportCompleted() {}

  private async waitForImportJobToFinish(): Promise<void> {
    const { tenant, landscape } = this.loginInfo;
    const pollingIntervalMs = 3000; // 3秒
    const timeoutMs = 20 * 60 * 1000; // 20分
    const startTime = Date.now();

    while (Date.now() - startTime < timeoutMs) {
      await new Promise(resolve => setTimeout(resolve, pollingIntervalMs));

      const result = await getImportJobStatus(tenant, landscape);
      const progress = result == null ? ImportJobStatus.notStarted : result;
      switch (progress) {
        case ImportJobStatus.completed:
          this.showSnackbarAndHideAfter3Sec(
            i18n.tc('jobScheduler.jobs.importJobCompleted')
          );
          this.emitImportCompleted();
          return;
        case ImportJobStatus.error422:
          this.showSnackbarAndHideAfter3Sec(
            i18n.tc('jobScheduler.jobs.importJobFailedStatus422')
          );
          return;
        case ImportJobStatus.error500:
          this.showSnackbarAndHideAfter3Sec(
            i18n.tc('jobScheduler.jobs.importJobFailedTimeout')
          );
          return;
        case ImportJobStatus.notStarted:
          return;
        case ImportJobStatus.started:
        default:
          break;
      }
    }
    this.showSnackbarAndHideAfter3Sec(
      i18n.tc('jobScheduler.jobs.importJobFailedTimeout')
    );
  }

  private showSnackbarAndHideAfter3Sec(message: string): void {
    globalSnackbarModule.hideProgressCircularAction();
    globalSnackbarModule.showSnackbarAction(message);
    setTimeout(() => {
      globalSnackbarModule.hideSnackbarAction();
    }, 3000);
  }

  private get isYamlOrCsv(): boolean {
    if (this.jobFile === null) return false;
    const extension = this.jobFile.name.slice(
      this.jobFile.name.lastIndexOf('.') + 1
    );
    return ['csv', 'yaml', 'yml'].includes(extension);
  }
}
